import { IntegrationConnection } from 'models/integrations/integrationConnection';
import { areDifferent } from 'utils/array';
import {
  AzureConnectionDTO,
  AzureDiscoveredSubscriptionDTO,
  AzureKnownSubscriptionDTO,
  AzureScopeDTO,
  AzureScopeEntryDTO,
} from '../AzureDTO';

//The Entra display name of the app the customer consents to and gives the Reader role; a product name, not translated.
export const AzureReportingAppName = 'ISOPlanner Azure reporting';

//The API's limits on the scope (the connection test refuses a bad scope with 400): the scope may hold up to 500
//decisions, so an organization with many subscriptions can exclude enough of them; a run checks at most 50
//subscriptions in scope.
export const MaxScopeEntries = 500;
export const MaxSubscriptionsInScope = 50;
export const MaxScopeReasonLength = 200;
export const MaxExcludedResourceGroups = 200; //per subscription

//A resource group of an in-scope subscription that is outside the ISMS scope, with its reason.
export class AzureExcludedResourceGroup {
  name: string = '';

  reason: string = '';

  decidedAtUtc?: string; //ISO, set by the web when the decision changes
}

//One decision of the coordinator: a subscription is in scope, or excluded with a reason.
export class AzureScopeEntry {
  subscriptionId: string = '';

  inScope: boolean = true;

  reason?: string; //required when excluded

  decidedAtUtc?: string; //ISO, set by the web when the decision changes

  //every other resource group of the subscription is in scope, so a new one is checked until someone excludes it
  excludedResourceGroups: AzureExcludedResourceGroup[] = [];
}

//A subscription the app could read at the last connection test, as Azure Resource Manager reports it.
export class AzureDiscoveredSubscription {
  subscriptionId: string = '';

  displayName: string = '';

  state: string = ''; //Enabled, Disabled, Warned, PastDue or Deleted

  tenantId: string = '';

  resourceGroups: string[] = []; //the resource group names the app sees in the subscription
}

//A subscription a connection test read at some time, kept by the API: one that is no longer discovered and not
//excluded has lost its read access (or was moved or deleted), even when the scope holds no decision for it.
export class AzureKnownSubscription {
  subscriptionId: string = '';

  displayName: string = '';

  lastSeenUtc?: string; //ISO, the last test that read it
}

//The Azure reporting connection, shared by the four Azure checks, in two parts: the scope is what the coordinator
//enters in the connection's form (IntegrationConnection.authData, saved like the SSL Labs form); what the API's
//connection test discovered is in IntegrationConnection.config, sent back as received on a save. Either part may be missing. An empty
//scope means every readable subscription is checked.
export class AzureConnectionConfig {
  scope: AzureScopeEntry[] = [];

  discovered?: AzureDiscoveredSubscription[]; //undefined until the first connection test after consent

  known: AzureKnownSubscription[] = []; //every subscription a test has read, also the ones it reads no more

  probedAtUtc?: string;

  //the customer's tenant and the object id of ISOPlanner's app there, from the connection test: the Reader role of step 2
  //is given to that principal
  tenantId?: string;

  principalId?: string;
}

//What the step-2 line under Authorization says, derived from the config and the connection's test outcome.
export enum AzureSetupState {
  None = 0, //never tested after consent: authorization (step 1) comes first
  WaitingForReadAccess = 1, //consent works, but no subscription is readable: nobody gave the Reader role yet
  SubscriptionNotReadable = 2, //a subscription in scope is no longer readable
  NewSubscription = 3, //the scope was reviewed, and a readable subscription has no decision yet
  Ready = 4,
  TooManyInScope = 5, //more subscriptions in scope than a run checks: every Azure check refuses to run
}

//subscription ids are GUIDs and resource group names are case-insensitive in Azure; Azure answers ids in lower case, a
//person may type them in any case
const isSameId = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

//Azure keeps a disabled or deleted subscription in the list, but nothing in it is read
const InactiveStates = ['disabled', 'deleted'];

const isActive = (subscription: AzureDiscoveredSubscription): boolean =>
  !InactiveStates.includes(subscription.state.toLowerCase());

//The subscriptions ISOPlanner reads: discovered at the last test, and not disabled or deleted.
export const getAzureReadableCount = (config: AzureConnectionConfig): number =>
  (config.discovered ?? []).filter(isActive).length;

export const isAzureSubscriptionDiscovered = (config: AzureConnectionConfig, subscriptionId: string): boolean =>
  (config.discovered ?? []).some((s) => isSameId(s.subscriptionId, subscriptionId));

const mapFromAzureScopeEntry = (dto: AzureScopeEntryDTO): AzureScopeEntry => {
  const entry = new AzureScopeEntry();
  entry.subscriptionId = dto.subscriptionId ?? '';
  entry.inScope = dto.inScope;
  entry.reason = dto.reason ?? undefined;
  entry.decidedAtUtc = dto.decidedAtUtc ?? undefined;
  entry.excludedResourceGroups = (dto.excludedResourceGroups ?? []).map((groupDto) => {
    const excluded = new AzureExcludedResourceGroup();
    excluded.name = groupDto.name ?? '';
    excluded.reason = groupDto.reason ?? '';
    excluded.decidedAtUtc = groupDto.decidedAtUtc ?? undefined;

    return excluded;
  });

  return entry;
};

const mapFromAzureKnownSubscription = (dto: AzureKnownSubscriptionDTO): AzureKnownSubscription => {
  const known = new AzureKnownSubscription();
  known.subscriptionId = dto.subscriptionId ?? '';
  known.displayName = dto.displayName ?? '';
  known.lastSeenUtc = dto.lastSeenUtc;

  return known;
};

const mapFromAzureDiscoveredSubscription = (dto: AzureDiscoveredSubscriptionDTO): AzureDiscoveredSubscription => {
  const subscription = new AzureDiscoveredSubscription();
  subscription.subscriptionId = dto.subscriptionId ?? '';
  subscription.displayName = dto.displayName ?? '';
  subscription.state = dto.state ?? '';
  subscription.tenantId = dto.tenantId ?? '';
  subscription.resourceGroups = dto.resourceGroups ?? [];

  return subscription;
};

//The connection's two parts: the scope the web saved (authData, an AzureScopeDTO) and what the API's connection test
//wrote (config, an AzureConnectionDTO). Either is absent on a connection never tested after consent.
export const getAzureConnectionConfig = (connection: IntegrationConnection | undefined): AzureConnectionConfig => {
  const scope = connection?.authData as AzureScopeDTO | undefined;
  const stored = connection?.config as AzureConnectionDTO | undefined;
  const config = new AzureConnectionConfig();
  config.scope = (scope?.subscriptions ?? []).map(mapFromAzureScopeEntry);
  config.known = (stored?.known ?? []).map(mapFromAzureKnownSubscription);

  const discovered = stored?.discovered;
  if (discovered) {
    config.probedAtUtc = discovered.probedAtUtc;
    config.tenantId = discovered.tenantId ?? undefined;
    config.principalId = discovered.principalId ?? undefined;
    config.discovered = (discovered.subscriptions ?? []).map(mapFromAzureDiscoveredSubscription);
  }

  return config;
};

export const getAzureSetupState = (
  config: AzureConnectionConfig,
  isAuthorized: boolean | undefined,
): AzureSetupState => {
  if (!config.discovered) return AzureSetupState.None;

  const discovered = config.discovered;
  if (isAuthorized === true && discovered.length === 0) return AzureSetupState.WaitingForReadAccess;

  //a subscription is in scope unless it is excluded (an empty scope means every one is in scope)
  const isExcluded = (id: string) => config.scope.some((entry) => !entry.inScope && isSameId(entry.subscriptionId, id));
  const isDiscovered = (id: string) => isAzureSubscriptionDiscovered(config, id);
  //one ISOPlanner read before counts too: the scope holds no decision for a subscription nobody reviewed
  if (
    config.scope.some((entry) => entry.inScope && !isDiscovered(entry.subscriptionId)) ||
    config.known.some((known) => !isDiscovered(known.subscriptionId) && !isExcluded(known.subscriptionId))
  ) {
    return AzureSetupState.SubscriptionNotReadable;
  }

  if (discovered.filter((s) => !isExcluded(s.subscriptionId)).length > MaxSubscriptionsInScope) {
    return AzureSetupState.TooManyInScope;
  }

  const hasEntry = (id: string) => config.scope.some((entry) => isSameId(entry.subscriptionId, id));
  if (config.scope.length > 0 && discovered.some((s) => !hasEntry(s.subscriptionId))) {
    return AzureSetupState.NewSubscription;
  }

  return isAuthorized === true ? AzureSetupState.Ready : AzureSetupState.None;
};

//One row of the scope dialog: every discovered subscription, every decided one that is no longer discovered, and
//every one ISOPlanner read before and reads no more.
export class AzureScopeRow {
  id: string = ''; //the subscription id

  displayName: string = '';

  isReadable: boolean = false;

  isInactive: boolean = false; //disabled or deleted in Azure: discovered, but nothing in it is read

  //read at an earlier test only, without a stored decision: Save stores one only when the user excludes it
  isKnownOnly: boolean = false;

  resourceGroups: string[] = []; //the resource groups discovered in the subscription

  entry?: AzureScopeEntry; //the stored decision
}

export const getAzureScopeRows = (config: AzureConnectionConfig): AzureScopeRow[] => {
  const knownName = (id: string): string =>
    config.known.find((known) => isSameId(known.subscriptionId, id))?.displayName || id;

  const rows: AzureScopeRow[] = (config.discovered ?? []).map((s) => {
    const row = new AzureScopeRow();
    row.id = s.subscriptionId;
    row.displayName = s.displayName || s.subscriptionId;
    row.isInactive = !isActive(s);
    row.isReadable = !row.isInactive;
    row.resourceGroups = s.resourceGroups;
    row.entry = config.scope.find((entry) => isSameId(entry.subscriptionId, s.subscriptionId));

    return row;
  });

  config.scope
    .filter((entry) => !rows.some((row) => isSameId(row.id, entry.subscriptionId)))
    .forEach((entry) => {
      const row = new AzureScopeRow();
      row.id = entry.subscriptionId;
      row.displayName = knownName(entry.subscriptionId);
      row.isReadable = false;
      row.entry = entry;
      rows.push(row);
    });

  //listed so the user can exclude it; the step-2 state says that it is no longer read
  config.known.forEach((known) => {
    if (rows.some((row) => isSameId(row.id, known.subscriptionId))) return;
    const row = new AzureScopeRow();
    row.id = known.subscriptionId;
    row.displayName = known.displayName || known.subscriptionId;
    row.isReadable = false;
    row.isKnownOnly = true;
    rows.push(row);
  });

  return rows;
};

export interface IAzureScopeDecision {
  inScope: boolean;
  reason?: string;
  excludedResourceGroups?: AzureExcludedResourceGroup[]; //the whole list when it was changed in the dialog
}

const isSameGroup = (a: AzureExcludedResourceGroup, b: AzureExcludedResourceGroup): boolean =>
  isSameId(a.name, b.name) && a.reason === b.reason;

//The excluded resource groups of a subscription with the dialog's changes, the changed ones stamped now.
export const buildExcludedResourceGroups = (
  stored: AzureExcludedResourceGroup[],
  decisions: Record<string, string | undefined>, //name -> reason when excluded, undefined when back in scope
  now: Date = new Date(),
): AzureExcludedResourceGroup[] => {
  const result = stored
    .filter((group) => !(group.name in decisions) || decisions[group.name] !== undefined)
    .map((group) => {
      const reason = group.name in decisions ? (decisions[group.name] ?? '').trim() : group.reason;
      const excluded = new AzureExcludedResourceGroup();
      excluded.name = group.name;
      excluded.reason = reason;
      excluded.decidedAtUtc = reason === group.reason ? group.decidedAtUtc : now.toISOString();

      return excluded;
    });

  Object.entries(decisions)
    .filter(([name, reason]) => reason !== undefined && !stored.some((group) => isSameId(group.name, name)))
    .forEach(([name, reason]) => {
      const excluded = new AzureExcludedResourceGroup();
      excluded.name = name;
      excluded.reason = (reason ?? '').trim();
      excluded.decidedAtUtc = now.toISOString();
      result.push(excluded);
    });

  return result;
};

//The scope a Save writes: an entry for every row (in scope unless excluded), stamped now when the decision is new or
//changed. Rows without a decision in the dialog keep their stored one, or become in scope. A subscription only read
//before gets an entry only when the user excludes it: without one it is in scope already, and opening the form
//would otherwise make a change to save.
export const buildAzureScope = (
  rows: AzureScopeRow[],
  decisions: Record<string, IAzureScopeDecision>,
  now: Date = new Date(),
): AzureScopeEntry[] => {
  return rows
    .filter((row) => !row.isKnownOnly || decisions[row.id]?.inScope === false)
    .map((row) => {
      const decision = decisions[row.id];
      const inScope = decision?.inScope ?? row.entry?.inScope ?? true;
      const reason = inScope ? undefined : (decision?.reason ?? row.entry?.reason)?.trim();
      const isChanged = !row.entry || row.entry.inScope !== inScope || (row.entry.reason ?? '') !== (reason ?? '');

      const entry = new AzureScopeEntry();
      entry.subscriptionId = row.id;
      entry.inScope = inScope;
      entry.reason = reason || undefined;
      entry.decidedAtUtc = isChanged ? now.toISOString() : row.entry?.decidedAtUtc;
      entry.excludedResourceGroups = decision?.excludedResourceGroups ?? row.entry?.excludedResourceGroups ?? [];

      return entry;
    });
};

export const isAzureScopeChanged = (stored: AzureScopeEntry[], next: AzureScopeEntry[]): boolean =>
  areDifferent(
    stored,
    next,
    (old, entry) =>
      isSameId(old.subscriptionId, entry.subscriptionId) &&
      old.inScope === entry.inScope &&
      (old.reason ?? '') === (entry.reason ?? '') &&
      !areDifferent(old.excludedResourceGroups, entry.excludedResourceGroups, isSameGroup),
  );

export const isAzureScopeValid = (scope: AzureScopeEntry[]): boolean => {
  if (scope.length > MaxScopeEntries) return false;

  const isReason = (reason: string | undefined): boolean => {
    const length = reason?.trim().length ?? 0;

    return length > 0 && length <= MaxScopeReasonLength;
  };

  return scope.every(
    (entry) =>
      (entry.inScope || isReason(entry.reason)) &&
      entry.excludedResourceGroups.length <= MaxExcludedResourceGroups &&
      entry.excludedResourceGroups.every((group) => isReason(group.reason)),
  );
};

//The form's auth data a Save sends: the scope. What the API discovered is not the form's and is not sent back changed.
export const toAzureScopeAuthData = (scope: AzureScopeEntry[]): Record<string, unknown> => {
  return {
    subscriptions: scope.map((entry) => ({
      subscriptionId: entry.subscriptionId,
      inScope: entry.inScope,
      reason: entry.reason,
      decidedAtUtc: entry.decidedAtUtc,
      excludedResourceGroups: entry.excludedResourceGroups.map((group) => ({
        name: group.name,
        reason: group.reason,
        decidedAtUtc: group.decidedAtUtc,
      })),
    })),
  };
};
